// Section copy: Editable via SiteSettings API. See CMS-CONTENT-PLAN.md item #9.
import { useEffect, useState } from "react";
import {
	Box,
	Container,
	Divider,
	Heading,
	HStack,
	Image,
	Stack,
	Text,
} from "@chakra-ui/react";
import { MdCheckCircle } from "react-icons/md";
import { useCmsApi } from "../../services/useCmsApi";

const steps = [
	{
		number: "1",
		title: "Share Your Field Location",
		details: ["Boundary files or field coordinates"],
	},
	{
		number: "2",
		title: "Share Agronomic Information (Optional)",
		details: [
			"What crops were planted the past five years?",
			"Top weed concerns?",
		],
	},
	{
		number: "3",
		title: "Geco Runs Your Analysis",
		details: ["You send the field details once. Geco handles the analysis and reviews the results with you."],
	},
	{
		number: "4",
		title: "Receive Your Prescriptions",
		details: [
			"Standard format - shapefile or similar",
			"Upload to existing equipment for automatic variable-rate application",
			"View on a tractor monitor or Google Maps for manual application",
		],
		imageUrl: "/get-started-field-map.png",
		imageAlt: "Field view with Geco map overlay",
	},
];

const DEFAULT_GET_STARTED_HEADING = "How to Get Started";

export const GetStartedStepsSection = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [heading, setHeading] = useState(DEFAULT_GET_STARTED_HEADING);

	useEffect(() => {
		cmsApi.getSiteSetting("getStartedHeading").then((s) => { if (s?.value) setHeading(s.value); }).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
	<Box py={{ base: "14", md: "24" }}>
		<Container maxW="1480px">
			<Stack spacing={{ base: "8", md: "10" }}>
				<Stack spacing="3" textAlign="center" align="center">
					<Heading size={{ base: "sm", md: "xl" }} color="gray.800">
						{heading}
					</Heading>
				</Stack>

				{/* ── Mobile: timeline layout ── Desktop: original card layout ── */}

				{/* MOBILE */}
				<Box display={{ base: "block", md: "none" }} position="relative">
					{/* Vertical connecting line */}
					<Box
						position="absolute"
						left="19px"
						top="16px"
						bottom="16px"
						w="2px"
						bg="blue.100"
						zIndex={0}
					/>
					<Stack spacing="0">
						{steps.map((step, index) => (
							<HStack key={step.number} align="flex-start" spacing="4" py="5" position="relative" zIndex={1}>
								{/* Number circle */}
								<Box
									flexShrink={0}
									w="40px"
									h="40px"
									borderRadius="full"
									bg="blue.500"
									color="white"
									fontWeight="bold"
									fontSize="sm"
									display="flex"
									alignItems="center"
									justifyContent="center"
									border="2px solid"
									borderColor="blue.300"
									boxShadow="sm"
								>
									{step.number}
								</Box>

								{/* Content — all flush at same left edge */}
								<Stack spacing="2" flex="1" pt="1">
									<Text
										fontWeight="700"
										fontSize="md"
										color="gray.800"
										lineHeight="1.3"
									>
										{step.title}
									</Text>
									<Stack spacing="1.5">
										{step.details.map((d) => (
											<HStack key={d} align="flex-start" spacing="2">
												<Box flexShrink={0} mt="3px">
													<MdCheckCircle color="#3182ce" size={14} />
												</Box>
												<Text fontSize="sm" color="gray.600" lineHeight="1.5">
													{d}
												</Text>
											</HStack>
										))}
									</Stack>
									{step.imageUrl && (
										<Box
											mt="2"
											p="2"
											bg="blue.50"
											borderRadius="xl"
											border="1px solid"
											borderColor="blue.100"
										>
											<Image
												src={step.imageUrl}
												alt={step.imageAlt}
												w="full"
												display="block"
												borderRadius="lg"
												loading="lazy"
											/>
										</Box>
									)}
								</Stack>
							</HStack>
						))}
					</Stack>
				</Box>

				{/* DESKTOP */}
				<Stack
					display={{ base: "none", md: "flex" }}
					w="full"
					bg="white"
					border="1px solid"
					borderColor="gray.200"
					borderRadius="2xl"
					p="8"
					spacing="0"
					boxShadow="sm"
				>
					{steps.map((step, index) => (
						<Box key={step.number}>
							<HStack
								spacing="6"
								align="flex-start"
								py="6"
							>
								<Box
									minW="72px"
									px="3"
									py="2"
									borderRadius="full"
									bg="blue.50"
									color="blue.500"
									fontWeight="extrabold"
									fontSize="xl"
									lineHeight="1"
									textAlign="center"
									flexShrink={0}
								>
									{step.number}
								</Box>
								<Stack spacing="4" flex="1" w="full">
									<Heading
										color="gray.800"
										fontSize={{ md: "2.35rem", lg: "2.7rem" }}
										lineHeight="1.02"
										letterSpacing="-0.02em"
									>
										{step.title}
									</Heading>
									<Stack spacing="2">
										{step.details.map((d) => (
											<HStack key={d} align="flex-start" spacing="3">
												<Box flexShrink={0} mt="0.3rem">
													<MdCheckCircle color="#3182ce" />
												</Box>
												<Text color="gray.600" fontSize="lg" lineHeight="1.55">
													{d}
												</Text>
											</HStack>
										))}
									</Stack>
									{step.imageUrl && (
										<Box
											mt="2"
											p="3"
											bg="blue.50"
											borderRadius="xl"
											border="1px solid"
											borderColor="blue.100"
										>
											<Image
												src={step.imageUrl}
												alt={step.imageAlt}
												w="full"
												display="block"
												mx="auto"
												borderRadius="lg"
												border="1px solid"
												borderColor="blue.200"
												loading="lazy"
											/>
										</Box>
									)}
								</Stack>
							</HStack>
							{index < steps.length - 1 ? <Divider borderColor="gray.200" /> : null}
						</Box>
					))}
				</Stack>
			</Stack>
		</Container>
	</Box>
	);
};
